import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, fmtTime, peso } from "@/data/tombBlessing";

const STATUS = {
  pending: ["Pending verification", "text-terracotta"],
  confirmed: ["Confirmed", "text-sage-dark"],
  completed: ["Blessing completed", "text-cocoa-700"],
  cancelled: ["Cancelled", "text-cocoa-700/60"],
};

const Row = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-sage-dark mb-1">{label}</div>
    <div className="font-serif text-lg text-cocoa-900 leading-snug">{value}</div>
  </div>
);

function Result({ d, onReset }) {
  const [label, color] = STATUS[d.status] || [d.status, "text-cocoa-700"];
  return (
    <div data-testid="tomb-lookup-result">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-5">
        <div>
          <h4 className="font-serif text-2xl text-cocoa-900">{d.requester_name}</h4>
          <div className="text-[11px] font-mono text-cocoa-700 mt-1">REF: {d.id.slice(0, 8).toUpperCase()}</div>
        </div>
        <div data-testid="tomb-lookup-status" className={`text-[11px] uppercase tracking-[0.28em] font-medium ${color}`}>● {label}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 border-y border-parchment-400 py-5">
        <Row label="Cemetery" value={d.cemetery} />
        <Row label="Date & Time" value={`${fmtDate(d.blessing_date)} · ${fmtTime(d.blessing_time)}`} />
        <Row label="For the soul(s) of" value={d.deceased_names.join(", ")} />
        <Row label="Donation" value={peso(d.donation_amount)} />
      </div>
      {d.parish_reply ? (
        <div className="mt-6 bg-parchment-50 border-l-2 border-terracotta p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">✉ Message from the Parish</div>
          <p className="font-serif italic text-cocoa-900 whitespace-pre-wrap">&ldquo;{d.parish_reply}&rdquo;</p>
          <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-cocoa-700">— {d.parish_reply_by || "Parish Office"}</div>
        </div>
      ) : (
        <p className="mt-6 text-sm italic text-sage-dark">✦ The Parish Office has not yet posted a reply.</p>
      )}
      <button onClick={onReset} data-testid="tomb-lookup-reset" className="mt-6 text-sm text-cocoa-700 hover:text-terracotta">Look up another request</button>
    </div>
  );
}

export default function TombBlessingLookup() {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [contact, setContact] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!ref.trim() || !contact.trim()) return toast.error("Please enter both your reference number and contact.");
    setLoading(true);
    try {
      const res = await api.post("/tomb-blessings/lookup", { ref: ref.trim(), contact: contact.trim() });
      setResult(res.data);
    } catch (err) {
      const s = err?.response?.status;
      toast.error(s === 403 ? "Contact number does not match this reference." : s === 404 ? "No request found with that reference." : "Could not load. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-4">
        <button type="button" data-testid="tomb-lookup-toggle" onClick={() => setOpen((v) => !v)} className="text-[11px] uppercase tracking-[0.3em] text-cocoa-800 hover:text-terracotta font-sans">
          ✉ Already requested? Check your blessing status <span className={`inline-block transition-transform ${open ? "rotate-180" : ""}`}>↓</span>
        </button>
      </div>
      {open && (
        <div className="bg-parchment-100 border border-parchment-400 p-6 md:p-8" data-testid="tomb-lookup-panel">
          {result ? (
            <Result d={result} onReset={() => { setResult(null); setRef(""); setContact(""); }} />
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-12 gap-4 items-end">
              <input data-testid="tomb-lookup-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Reference no." className="sm:col-span-5 field font-mono uppercase" />
              <input data-testid="tomb-lookup-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact number" className="sm:col-span-4 field" />
              <button type="submit" disabled={loading} data-testid="tomb-lookup-submit" className="sm:col-span-3 bg-cocoa-900 text-parchment-100 px-5 py-3 text-sm font-sans hover:bg-terracotta transition-colors disabled:opacity-60">
                {loading ? "Looking up…" : "Check status"}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
